import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { IoFunnel } from "react-icons/io5";
import { Row, Col } from "react-bootstrap";

import { orderBy } from "lodash";

import moment from "moment";

import ExpenseService from "../../services/expenseService";

import * as callActions from "../../actions/callActions";
import * as modalActions from "../../actions/modalActions";

import * as customerActions from "../../actions/customerActions";
import * as projectActions from "../../actions/projectActions";
import * as userActions from "../../actions/userActions";
import * as serviceActions from "../../actions/serviceActions";
import * as codeActions from "../../actions/codeActions";
import * as expenseActions from "../../actions/expenseActions";

import HeaderWrapper from "../common/HeaderWrapper";
import ExpenseApprovalContainer from "./ExpenseApprovalContainer";
import FiltersComponent from "../common/FiltersComponent";
import ExpenseUserContainer from "./ExpenseUserContainer";
import ConfirmModal from "../common/ConfirmModal";
import Loading from "../common/Loading";
import { customToast as toast } from "../../utils";
import { downloadCSV } from "../reports/utils";
import { formatDate } from "../../helpers/date";

const ExpenseApprovalPage = ({
  auth,
  actions,
  history,
  expenseToDelete,
  codes,
  allUsers
}) => {
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(
    localStorage.getItem("expense-approval-filters")
      ? JSON.parse(localStorage.getItem("expense-approval-filters"))
      : {
          customer: [],
          employee: [],
          startDate: null,
          endDate: null
        }
  );

  const [expenses, setExpenses] = useState([]);
  const [expensesToUpdate, setExpensesToUpdate] = useState([]);
  const [focusedInput, setFocusedInput] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);

    const approvedStatus = codes.find(
      (c) => c.label === "Approved" && c.entity === "Expenses"
    );
    const submittedStatus = codes.find(
      (c) => c.label === "Submitted" && c.entity === "Expenses"
    );
    const newFilters = localStorage.getItem("expense-approval-filters")
      ? JSON.parse(localStorage.getItem("expense-approval-filters"))
      : {
          ...filters,
          status: [
            { value: approvedStatus?.id, label: "Not Yet Completed" },
            { value: submittedStatus?.id }
          ]
        };
    const newExpenses = await ExpenseService.loadExpensesByFilters({
      ...newFilters,
      grouped_by_employee: newFilters.grouped_by_employee ? "employee" : null
    });
    await setExpenses(newExpenses);
    await setFilters(newFilters);
    setLoading(false);
  }, [codes, filters]);

  useEffect(() => {
    setLoading(true);
    if (codes?.length && !expenses?.length) {
      fetchData();
    }
    if (!allUsers.length) actions.loadAllUsers();
    if (!codes && codes.length === 0) actions.loadCodes();
    setLoading(false);
  }, [codes, allUsers, actions, fetchData, expenses]);

  const onClickView = (id) => {
    history.push(`/app/expenses/${id}`);
  };

  const onClickDelete = (id) => {
    actions.requestExpenseId(id);
  };

  const handleDelete = async () => {
    await actions.deleteExpense(expenseToDelete);
    await fetchData();
  };

  const handleChangeFilters = async (field, input, approved) => {
    let statusFilter;
    setLoading(true);
    const approvedStatus = codes.find(
      (c) => c.label === "Approved" && c.entity === "Expenses"
    );
    const submittedStatus = codes.find(
      (c) => c.label === "Submitted" && c.entity === "Expenses"
    );

    if (field === "status") {
      statusFilter = [input];
      if (statusFilter[0].value === approvedStatus?.id) {
        statusFilter[1] = { value: submittedStatus?.id };
      }
    } else if (field === "ApprovedStatus") {
      if (approved) {
        statusFilter = [
          {
            value: approvedStatus?.id,
            label: "Not Yet Completed"
          }
        ];
      } else {
        statusFilter = [
          {
            value: approvedStatus?.id,
            label: "Not Yet Completed"
          },
          { value: submittedStatus?.id }
        ];
      }
    } else {
      statusFilter = input;
    }
    let fieldToFilter = field;
    if (fieldToFilter === "ApprovedStatus") {
      fieldToFilter = "status";
    }

    await setFilters({ ...filters, [fieldToFilter]: statusFilter });
    await localStorage.setItem(
      "expense-approval-filters",
      JSON.stringify({ ...filters, [fieldToFilter]: statusFilter })
    );
    fetchData();

    if (field === "grouped_by_employee") {
      setExpensesToUpdate([]);
    }
  };

  const handleChangeDate = async (startDate, endDate) => {
    await setFilters({
      ...filters,
      ...(startDate && { startDate: moment(startDate).startOf("day") }),
      ...(endDate && { endDate: moment(endDate).endOf("day") })
    });
    await localStorage.setItem(
      "expense-approval-filters",
      JSON.stringify({
        ...filters,
        ...(startDate && { startDate: moment(startDate).startOf("day") }),
        ...(endDate && { endDate: moment(endDate).endOf("day") })
      })
    );
    fetchData();
  };

  const resetFilters = async () => {
    setLoading(true);
    const approvedStatus = codes.find(
      (c) => c.label === "Approved" && c.entity === "Expenses"
    );
    const submittedStatus = codes.find(
      (c) => c.label === "Submitted" && c.entity === "Expenses"
    );
    await setFilters({
      customer: [],
      employee: [],
      department: [],
      startDate: null,
      endDate: null,
      status: [
        { value: approvedStatus?.id, label: "Not Yet Completed" },
        { value: submittedStatus?.id }
      ]
    });
    await localStorage.setItem(
      "expense-approval-filters",
      JSON.stringify({
        customer: [],
        employee: [],
        department: [],
        startDate: null,
        endDate: null,
        status: [
          { value: approvedStatus?.id, label: "Not Yet Completed" },
          { value: submittedStatus?.id }
        ]
      })
    );
    fetchData();
  };

  const openFilters = () => {
    setOpen(!open);
  };

  const updateExpense = async (newExpense) => {
    try {
      if (filters.grouped_by_employee) {
        const employeeExpenses = expenses.find(
          (expense) => expense.user._id === newExpense.employee
        );
        const newExpensesToUpdate = employeeExpenses.details.map((expense) =>
          expense._id === newExpense._id ? newExpense : expense
        );
        const newExpenses = expenses.map((expense) =>
          expense.user._id === newExpense.employee
            ? { ...expense, details: newExpensesToUpdate }
            : expense
        );
        setExpenses(newExpenses);
      } else {
        const newExpenses = expenses.map((expense) =>
          expense._id === newExpense._id ? newExpense : expense
        );
        setExpenses(newExpenses);
      }

      if (expensesToUpdate?.length) {
        const existingExpense = expensesToUpdate.find(
          (expense) => expense._id === newExpense._id
        );
        if (existingExpense) {
          const newExpensesToUpdate = expensesToUpdate.map((expense) =>
            expense._id === newExpense._id ? newExpense : expense
          );
          setExpensesToUpdate(newExpensesToUpdate);
        } else {
          setExpensesToUpdate([...expensesToUpdate, newExpense]);
        }
      } else {
        setExpensesToUpdate([newExpense]);
      }
    } catch (error) {
      toast.error();
    }
  };

  const resetSelectedExpenses = () => {
    setExpensesToUpdate([]);
    fetchData();
  };

  const saveChanges = async () => {
    await actions.updateManyExpenses(expensesToUpdate);
    setExpensesToUpdate([]);
    await fetchData();
  };

  const markExpenseAsComplete = async (newExpense) => {
    await actions.updateExpense(newExpense);
    await fetchData();
  };

  const onCSVExport = () => {
    const columns = [
      { Header: "Date", accessor: "date" },
      { Header: "Customer", accessor: "customer.name" },
      { Header: "Project", accessor: "project.name" },
      { Header: "Total", accessor: "total" },
      { Header: "Employee", accessor: "employee.full_name" },
      { Header: "Status", accessor: "status.label" },
      { Header: "Category", accessor: "category.label" },
      { Header: "Description", accessor: "description" },
      { Header: "Bill to Customer", accessor: "bill_to_customer" },
      { Header: "Reimburse to Employee", accessor: "reimburse_to_employee" },
      { Header: "To Customer Complete", accessor: "to_customer_complete" },
      { Header: "To Employee Complete", accessor: "to_employee_complete" },
      { Header: "Occurs Annually", accessor: "occurs_annually" }
    ];
    downloadCSV(
      "Expense Approval",
      expenses.map((expense) => ({
        ...expense,
        date: formatDate(expense.date, "MM/dd/yyy"),
        bill_to_customer: expense.bill_to_customer ? "yes" : "no",
        reimburse_to_employee: expense.reimburse_to_employee ? "yes" : "no",
        to_customer_complete: expense.to_customer_complete ? "yes" : "no",
        to_employee_complete: expense.to_employee_complete ? "yes" : "no",
        occurs_annually: expense.occurs_annually ? "yes" : "no"
      })),
      columns,
      "Expense Approval"
    );
  };

  return (
    <React.Fragment>
      <HeaderWrapper
        breadcrumbs={[{ label: "Expense Approval" }]}
        onClickAction={[
          { action: openFilters, icon: <IoFunnel /> },
          {
            action: () => onCSVExport(),
            className: "btn-success",
            text: "CSV",
            disabled: loading || !expenses.length
          }
        ]}
      />
      <Row>
        <Col md={12}>
          {filters.status && (
            <React.Fragment>
              <FiltersComponent
                open={open}
                filters={filters}
                focusedInput={focusedInput}
                onChangeFilters={handleChangeFilters}
                onChangeDate={handleChangeDate}
                onFocusChange={(fi) => setFocusedInput(fi)}
                onReset={resetFilters}
                fields={[
                  "Customer",
                  "Department",
                  "expense_date",
                  "Employee",
                  "expenseStatus",
                  "Approved",
                  "Billed",
                  "Reimbursed",
                  "GroupByEmployee"
                ]}
              />
            </React.Fragment>
          )}
        </Col>
      </Row>
      <div className="content-wrapper">
        {loading && (
          <div className="loading-loader-load-container">
            <Loading active={loading} />
          </div>
        )}
        {expensesToUpdate?.length ? (
          <div className="flex -end">
            <button
              className="btn btn-warning"
              type="button"
              onClick={resetSelectedExpenses}
            >
              Discard
            </button>
            <button
              className="btn btn-success"
              type="button"
              onClick={saveChanges}
            >
              Save
            </button>
          </div>
        ) : null}
        {!loading &&
          !!expenses.length &&
          !expenses[0].details &&
          codes &&
          !filters.grouped_by_employee &&
          orderBy(expenses, "date", "asc").map((expense) => (
            <div key={expense._id}>
              <ExpenseApprovalContainer
                auth={auth}
                expense={expense}
                updateExpense={updateExpense}
                onClickView={onClickView}
                onClickDelete={onClickDelete}
                reimbursed={expense.to_employee_complete}
                billed={expense.to_customer_complete}
                codes={codes}
                onMarkAsComplete={markExpenseAsComplete}
              />
            </div>
          ))}
        {!loading &&
          expenses &&
          codes &&
          filters.grouped_by_employee &&
          expenses.map((expense) => (
            <ExpenseUserContainer
              key={expense.user?.id}
              user={allUsers.find((user) => user._id === expense.user?._id)}
              expenses={expense.details}
              updateExpense={updateExpense}
              onClickView={onClickView}
              onClickDelete={onClickDelete}
              codes={codes}
              onMarkAsComplete={markExpenseAsComplete}
            />
          ))}
      </div>
      <ConfirmModal
        id="expenseDeleteModal"
        title="Delete Expense"
        body="Are you sure you want to delete this expense?"
        close={actions.hideModal}
        confirm={handleDelete}
      />
    </React.Fragment>
  );
};

ExpenseApprovalPage.propTypes = {
  auth: PropTypes.object.isRequired,
  actions: PropTypes.object,
  history: PropTypes.object,
  expenseToDelete: PropTypes.string,
  codes: PropTypes.array,
  allUsers: PropTypes.array
};

function mapStatesToProps(state) {
  return {
    state: state.reducers,
    auth: state.reducers.auth,
    expensesToApprove: state.reducers.expensesToApprove,
    codes: state.reducers.codes,
    expenses: state.reducers.expenses,
    expenseToDelete: state.reducers.expenseToDelete,
    allUsers: state.reducers.allUsers
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...modalActions,
        ...callActions,
        ...customerActions,
        ...projectActions,
        ...serviceActions,
        ...userActions,
        ...codeActions,
        ...expenseActions
      },
      dispatch
    )
  };
}

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(ExpenseApprovalPage);
